<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Verträge</h1>
      </v-col>
      <v-col class="text-right">
        <v-btn
          text
          :href="contractRequestFormUrl"
          target="_blank"
          class="mr-2"
        >
          <v-icon left small>{{ mdiOpenInNew }}</v-icon>
          Formular Netzbetrieb
        </v-btn>
        <v-btn
          color="primary"
          @click="openCsvImport"
        >
          Neuer Vertrag
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="pendingContractRequests.length" class="mb-2">
      <v-col cols="12">
        <div class="text-h6 mb-3">
          Offene Vertragsanfragen
          <v-chip small class="ml-1">{{ pendingContractRequests.length }}</v-chip>
        </div>
        <v-expansion-panels v-model="openPendingPanels" multiple>
          <v-expansion-panel
            v-for="item in pendingContractRequests"
            :key="item.id"
          >
            <v-expansion-panel-header>
              <v-row no-gutters align="center">
                <v-col cols="4">
                  <strong>{{ item.organization_name }}</strong>
                  <span class="grey--text caption ml-2">{{ item.legal_form }}</span>
                </v-col>
                <v-col cols="2" class="grey--text caption">
                  {{ momentize(item.created_at) }}
                </v-col>
                <v-col>
                  <v-chip
                    v-for="p in [...(item.requestedEcomProducts || []), ...(item.requestedDeviceProducts || [])]"
                    :key="p.deviceClass"
                    x-small outlined class="mr-1"
                  >{{ p.label }}</v-chip>
                </v-col>
                <v-col class="shrink pr-2" @click.stop>
                  <v-tooltip bottom :disabled="!!effectiveCreditorId(item)">
                    <template #activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs">
                        <v-btn
                          color="success"
                          small
                          :loading="acceptingCustomerId === item.id"
                          :disabled="!effectiveCreditorId(item)"
                          @click.stop="acceptContractRequest(item)"
                        >
                          Annehmen
                        </v-btn>
                      </span>
                    </template>
                    <span>Bitte zuerst Gläubiger-ID eintragen</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="12" sm="6">
                  <div class="subtitle-2 mb-2">Organisation</div>
                  <table class="caption" style="border-spacing: 4px 2px">
                    <tr><td class="grey--text pr-3">Name</td><td>{{ item.organization_name }}</td></tr>
                    <tr>
                      <td class="grey--text pr-3" style="vertical-align: middle">Kurzname</td>
                      <td>
                        <v-text-field
                          :value="pendingShortOrganizationNames[item.id] !== undefined ? pendingShortOrganizationNames[item.id] : (item.short_organization_name || '')"
                          @input="$set(pendingShortOrganizationNames, item.id, $event)"
                          dense
                          hide-details
                          style="max-width: 220px"
                        />
                      </td>
                    </tr>
                    <tr><td class="grey--text pr-3">Rechtsform</td><td>{{ item.legal_form }}</td></tr>
                    <tr v-if="item.region"><td class="grey--text pr-3">Landeskirche</td><td>{{ item.region }}</td></tr>
                    <tr v-if="item.district"><td class="grey--text pr-3">Kirchenkreis</td><td>{{ item.district }}</td></tr>
                    <tr v-if="item.founding_date"><td class="grey--text pr-3">Gründungsdatum</td><td>{{ momentize(item.founding_date) }}</td></tr>
                    <tr v-if="item.register"><td class="grey--text pr-3">Register</td><td>{{ item.register }}</td></tr>
                    <tr v-if="item.register_number"><td class="grey--text pr-3">Register-Nr.</td><td>{{ item.register_number }}</td></tr>
                    <tr v-if="item.register_court"><td class="grey--text pr-3">Registergericht</td><td>{{ item.register_court }}</td></tr>
                    <tr v-if="item.website_url"><td class="grey--text pr-3">Website</td><td><a :href="item.website_url" target="_blank">{{ item.website_url }}</a></td></tr>
                    <tr>
                      <td class="grey--text pr-3" style="vertical-align: middle">Gläubiger-ID *</td>
                      <td>
                        <v-text-field
                          :value="pendingCreditorIds[item.id] !== undefined ? pendingCreditorIds[item.id] : (item.creditor_id || '')"
                          @input="$set(pendingCreditorIds, item.id, $event)"
                          dense
                          hide-details
                          placeholder="Pflichtfeld"
                          style="max-width: 220px"
                        />
                      </td>
                    </tr>
                    <tr v-if="item.parent_organization_name"><td class="grey--text pr-3">Mutterorganisation</td><td>{{ item.parent_organization_name }}</td></tr>
                  </table>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="subtitle-2 mb-2">Personen</div>
                  <div
                    v-for="personUrl in item.persons"
                    :key="personUrl"
                    class="mb-3"
                  >
                    <template v-if="contractPersonDataByUrl[personUrl]">
                      <div class="body-2">
                        <strong>{{ contractPersonDataByUrl[personUrl].first_name }} {{ contractPersonDataByUrl[personUrl].last_name }}</strong>
                        <v-chip
                          v-for="t in contractPersonDataByUrl[personUrl].types"
                          :key="t"
                          x-small class="ml-1"
                        >{{ pendingPersonTypeLabel(t) }}</v-chip>
                      </div>
                      <table class="caption" style="border-spacing: 4px 2px">
                        <tr v-if="contractPersonDataByUrl[personUrl].email">
                          <td class="grey--text pr-3">E-Mail</td>
                          <td>{{ contractPersonDataByUrl[personUrl].email }}</td>
                        </tr>
                        <tr v-if="contractPersonDataByUrl[personUrl].phone_number">
                          <td class="grey--text pr-3">Telefon</td>
                          <td>{{ contractPersonDataByUrl[personUrl].phone_number }}</td>
                        </tr>
                        <tr v-if="contractPersonDataByUrl[personUrl].role">
                          <td class="grey--text pr-3">Funktion</td>
                          <td>{{ contractPersonDataByUrl[personUrl].role }}</td>
                        </tr>
                        <tr v-if="contractPersonDataByUrl[personUrl].legitimization && contractPersonDataByUrl[personUrl].legitimization.date_of_birth">
                          <td class="grey--text pr-3">Geburtsdatum</td>
                          <td>{{ momentize(contractPersonDataByUrl[personUrl].legitimization.date_of_birth) }}</td>
                        </tr>
                        <tr v-if="contractPersonDataByUrl[personUrl].legitimization && contractPersonDataByUrl[personUrl].legitimization.document_type">
                          <td class="grey--text pr-3">Ausweis</td>
                          <td>{{ contractPersonDataByUrl[personUrl].legitimization.document_type }} {{ contractPersonDataByUrl[personUrl].legitimization.document_no }}</td>
                        </tr>
                      </table>
                    </template>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="subtitle-2 mb-2">Bankverbindungen</div>
                  <div v-if="loadingBankAccounts[item.url]">
                    <v-progress-circular indeterminate size="16" width="2" />
                  </div>
                  <template v-else-if="pendingBankAccounts[item.url]">
                    <div
                      v-for="ba in pendingBankAccounts[item.url]"
                      :key="ba.id"
                      class="mb-3"
                    >
                      <div class="body-2">
                        <v-chip v-for="t in ba.types" :key="t" x-small class="mr-1">{{ pendingBankTypeLabel(t) }}</v-chip>
                      </div>
                      <table class="caption" style="border-spacing: 4px 2px">
                        <tr v-if="ba.owner"><td class="grey--text pr-3">Inhaber</td><td>{{ ba.owner }}</td></tr>
                        <tr v-if="ba.iban"><td class="grey--text pr-3">IBAN</td><td>{{ ba.iban }}</td></tr>
                        <tr v-if="ba.bank_name"><td class="grey--text pr-3">Bank</td><td>{{ ba.bank_name }}</td></tr>
                      </table>
                    </div>
                  </template>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="subtitle-2 mb-2">Sonstiges</div>
                  <table class="caption" style="border-spacing: 4px 2px">
                    <tr><td class="grey--text pr-3">Eingereicht am</td><td>{{ momentize(item.created_at) }}</td></tr>
                    <tr v-if="item.comments"><td class="grey--text pr-3">Kommentar</td><td>{{ item.comments }}</td></tr>
                    <tr v-if="item.discount_code"><td class="grey--text pr-3">Aktionscode</td><td>{{ item.discount_code }}</td></tr>
                    <tr v-if="item.referral_source"><td class="grey--text pr-3">Referral</td><td>{{ item.referral_source }}</td></tr>
                    <tr><td class="grey--text pr-3">Datenschutz akzeptiert</td><td>{{ item.accept_data_processing ? 'Ja' : 'Nein' }}</td></tr>
                  </table>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="d-flex justify-end">
                  <v-tooltip bottom :disabled="!!effectiveCreditorId(item)">
                    <template #activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs">
                        <v-btn
                          color="success"
                          :loading="acceptingCustomerId === item.id"
                          :disabled="!effectiveCreditorId(item)"
                          @click="acceptContractRequest(item)"
                        >
                          Vertragsanfrage annehmen
                        </v-btn>
                      </span>
                    </template>
                    <span>Bitte zuerst Gläubiger-ID eintragen</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row>
      <v-data-table
        :headers="headers"
        :items="activeContracts"
        sort-by="created_at"
        :sort-desc="true"
      >
        <template #item.organization_name="{ item }">
          <div class="d-flex align-center">
            <router-link :to="{ name: 'organization.dashboard', params: { id: item.organizationId } }">
              {{ item.organization_name }}
            </router-link>
            <v-btn icon x-small class="ml-1" @click.prevent="openThirdPartyFormModal(item)">
              <v-icon x-small>{{ mdiClipboardOutline }}</v-icon>
            </v-btn>
          </div>
        </template>
        <template #item.created_at="{ item }">
          {{ momentize(item.created_at) }}
        </template>
        <template #item._offer_devices="{ item }">
          <div class="d-flex flex-row align-center grid-gap">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  color="primary"
                  small
                  class="px-2"
                  style="min-width: 0"
                  v-on="on"
                  v-bind="attrs"
                  @click="openDeviceEdit(item)"
                >
                  <v-icon small :left="!item.requestedDeviceProducts.length">{{ mdiPencil }}</v-icon>
                  <span v-if="!item.requestedDeviceProducts.length">Gerät hinzufügen</span>
                </v-btn>
              </template>
              <span>Geräteangebot bearbeiten</span>
            </v-tooltip>
            <div class="related-devices">
              <div
                v-for="(device, index) in item.requestedDeviceProducts"
                :key="index"
              >
                <device-icon :device-class="device.deviceClass"></device-icon>
                {{ device.count }}
              </div>
            </div>
            <span
              v-if="item.relatedFileDevices"
              class="related-file-name"
            >
              {{ item.relatedFileDevices.name.replace('Anlage 7 - ', '') }}
            </span>
            <v-tooltip v-else-if="item.requestedDeviceProducts.length" bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  color="primary"
                  small
                  v-on="on"
                  v-bind="attrs"
                  @click="initializeAttachmentUpload(item, RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES)"
                >
                  <v-icon small left>{{ mdiUpload }}</v-icon>
                  Upload AG
                </v-btn>
              </template>
              <span>Anlage für Geräte hochladen</span>
            </v-tooltip>
          </div>
        </template>
        <template #item.relatedFileEcom="{ item }">
          <div class="d-flex flex-row align-center grid-gap">
            <div class="related-devices">
              <div
                v-for="(device, index) in item.requestedEcomProducts"
                :key="index"
              >
                <device-icon :device-class="device.deviceClass"></device-icon>
                {{ device.count }}
              </div>
            </div>
            <span
              v-if="item.relatedFileEcom"
              class="related-file-name"
            >
              {{ item.relatedFileEcom.name.replace('Anlage 8 - ', '') }}
            </span>
            <v-tooltip v-else-if="item.requestedEcomProducts.length" bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  color="primary"
                  small
                  v-on="on"
                  v-bind="attrs"
                  @click="initializeAttachmentUpload(item, RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM)"
                >
                  <v-icon small left>{{ mdiUpload }}</v-icon>
                  Upload AG
                </v-btn>
              </template>
              <span>Anlage für Ecommerce hochladen</span>
            </v-tooltip>
          </div>
        </template>
        <template #item._person1="{ item }">
          <div v-if="item.relevantPeople[0]" class="d-flex align-center">
            <span
              class="person-link"
              @click="openPersonModal(item.relevantPeopleUrls[0])"
            >
              {{ item.relevantPeople[0] }}
            </span>
            <v-icon
              v-if="item.relevantPeopleLegitimizationStatus[0] === 'verified'"
              small
              color="success"
              class="ml-1"
            >
              {{ mdiCheckCircle }}
            </v-icon>
            <v-icon
              v-else-if="item.relevantPeopleLegitimizationStatus[0] === 'pending'"
              small
              color="warning"
              class="ml-1"
            >
              {{ mdiClockOutline }}
            </v-icon>
          </div>
        </template>
        <template #item._person2="{ item }">
          <div v-if="item.relevantPeople[1]" class="d-flex align-center">
            <span
              class="person-link"
              @click="openPersonModal(item.relevantPeopleUrls[1])"
            >
              {{ item.relevantPeople[1] }}
            </span>
            <v-icon
              v-if="item.relevantPeopleLegitimizationStatus[1] === 'verified'"
              small
              color="success"
              class="ml-1"
            >
              {{ mdiCheckCircle }}
            </v-icon>
            <v-icon
              v-else-if="item.relevantPeopleLegitimizationStatus[1] === 'pending'"
              small
              color="warning"
              class="ml-1"
            >
              {{ mdiClockOutline }}
            </v-icon>
          </div>
        </template>
        <template #item._person3="{ item }">
          <div v-if="item.relevantPeople[2]" class="d-flex align-center">
            <span
              class="person-link"
              @click="openPersonModal(item.relevantPeopleUrls[2])"
            >
              {{ item.relevantPeople[2] }}
            </span>
            <v-icon
              v-if="item.relevantPeopleLegitimizationStatus[2] === 'verified'"
              small
              color="success"
              class="ml-1"
            >
              {{ mdiCheckCircle }}
            </v-icon>
            <v-icon
              v-else-if="item.relevantPeopleLegitimizationStatus[2] === 'pending'"
              small
              color="warning"
              class="ml-1"
            >
              {{ mdiClockOutline }}
            </v-icon>
          </div>
        </template>
        <template #item._actions="{ item }">
          <div class="d-flex align-center">
            <v-tooltip
              v-if="item.generatedContractBundle"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="item.isContractRegenerating"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="regenerateContract(item)"
                >
                  <v-icon>{{ mdiReload }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isContractRegenerating ? 'Vertrag wird neu erzeugt': 'Vertrag neu erzeugen' }}</span>
            </v-tooltip>
            <v-tooltip
              v-else
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="item.isContractRegenerating"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="generateContract(item)"
                >
                  <v-icon>{{ mdiFileDocumentPlusOutline }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isContractRegenerating ? 'Vertrag wird erzeugt': 'Vertrag erzeugen' }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="!item.generatedContractBundle || item.isContractRegenerating"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="downloadContract(item)"
                >
                  <v-icon>{{ mdiDownloadOutline }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isContractRegenerating ? 'Vertrag wird neu erzeugt' : (item.generatedContractBundle ? 'Vertrag vom ' + momentize(item.generatedContractBundle.created_at) + ' herunterladen' : 'Kein Vertrag verfügbar') }}</span>
            </v-tooltip>
            <span v-if="item.generatedContractBundle" class="caption grey--text align-self-center">
              {{ momentize(item.generatedContractBundle.created_at) }}
            </span>
          </div>
        </template>
      </v-data-table>
      <v-file-input
        v-show="false"
        v-model="uploadFile"
        id="related-file-chooser"
        ref="relatedFileChooser"
        outlined
        accept="application/pdf"
        label="Datei auswählen"
      ></v-file-input>
      <v-file-input
        v-show="false"
        v-model="csvImportFile"
        id="csv-import-chooser"
        ref="csvImportChooser"
        outlined
        accept=".csv,text/csv"
        label="CSV-Datei auswählen"
      ></v-file-input>
    </v-row>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Schließen
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog
      v-model="deviceEditDialog.show"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          Geräteangebot bearbeiten
          <v-spacer></v-spacer>
          <v-btn icon @click="deviceEditDialog.show = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="subtitle-2 mb-2">Geräte</div>
          <v-row
            v-for="(label, key) in CONTRACT_PRODUCT_CLASSES"
            :key="key"
            v-show="!ECOM_DEVICE_LIST.includes(key)"
            dense
          >
            <v-col cols="7">{{ label }}</v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="deviceEditDialog.editData[key.toLowerCase()]"
                type="number"
                min="0"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <div class="subtitle-2 mt-4 mb-2">Ecommerce</div>
          <v-row
            v-for="(label, key) in CONTRACT_PRODUCT_CLASSES"
            :key="'ecom-' + key"
            v-show="ECOM_DEVICE_LIST.includes(key)"
            dense
          >
            <v-col cols="7">{{ label }}</v-col>
            <v-col cols="5">
              <v-text-field
                v-model.number="deviceEditDialog.editData[key.toLowerCase()]"
                type="number"
                min="0"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deviceEditDialog.show = false">Abbrechen</v-btn>
          <v-btn color="primary" :loading="deviceEditDialog.saving" @click="saveDeviceData">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="thirdPartyFormModal.show"
      max-width="500px"
    >
      <v-card v-if="thirdPartyFormModal.item">
        <v-card-title>
          Daten für VR Payment - Webportal
          <v-spacer></v-spacer>
          <v-btn icon @click="thirdPartyFormModal.show = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="subtitle-2 mb-3">Adresse Händler</div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Organisationsname</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormModal.item.organization_name }}</div>
            <v-btn icon x-small @click="copyField('org_name', thirdPartyFormModal.item.organization_name)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'org_name' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'org_name' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Kontaktperson</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormModal.item.relevantPeople[0] || '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormModal.item.relevantPeople[0]" @click="copyField('contact', thirdPartyFormModal.item.relevantPeople[0])">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'contact' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'contact' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Straße</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormAddressSplit.street || '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormAddressSplit.street" @click="copyField('street', thirdPartyFormAddressSplit.street)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'street' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'street' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Hausnummer</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormAddressSplit.nr || '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormAddressSplit.nr" @click="copyField('nr', thirdPartyFormAddressSplit.nr)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'nr' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'nr' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">PLZ</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormAddress ? thirdPartyFormAddress.postcode : '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormAddress" @click="copyField('zip', thirdPartyFormAddress.postcode)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'zip' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'zip' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Ort</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormAddress ? thirdPartyFormAddress.locality : '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormAddress" @click="copyField('city', thirdPartyFormAddress.locality)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'city' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'city' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-start mt-3">
            <div class="form-copy-label caption grey--text text--darken-1 mt-1">Branche</div>
            <div class="flex-grow-1">
              <div class="d-flex align-center mb-1">
                <span class="mr-1 body-2">8640</span>
                <v-btn icon x-small @click="copyField('branch8640', '8640')">
                  <v-icon small :color="thirdPartyFormModal.copiedField === 'branch8640' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'branch8640' ? mdiCheck : mdiContentCopy }}</v-icon>
                </v-btn>
                <span class="ml-2 caption">Verein</span>
              </div>
              <div class="d-flex align-center">
                <span class="mr-1 body-2">8660</span>
                <v-btn icon x-small @click="copyField('branch8660', '8660')">
                  <v-icon small :color="thirdPartyFormModal.copiedField === 'branch8660' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'branch8660' ? mdiCheck : mdiContentCopy }}</v-icon>
                </v-btn>
                <span class="ml-2 caption">Religionsgemeinschaften</span>
              </div>
            </div>
          </div>

          <v-alert v-if="!thirdPartyFormAddress" type="warning" dense class="mt-4">
            Keine Adressdaten verfügbar.
          </v-alert>

          <v-divider class="my-4"></v-divider>
          <div class="subtitle-2 mb-3">Bank Händler</div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">IBAN (SEPA-Mandat)</div>
            <div class="form-copy-value flex-grow-1">
              <v-progress-circular v-if="thirdPartyFormModal.bankDataLoading" indeterminate size="16" width="2"></v-progress-circular>
              <span v-else>{{ thirdPartyFormModal.bankIban || '-' }}</span>
            </div>
            <v-btn icon x-small :disabled="!thirdPartyFormModal.bankIban" @click="copyField('iban', thirdPartyFormModal.bankIban)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'iban' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'iban' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Kontoinhaber</div>
            <div class="form-copy-value flex-grow-1">
              <v-progress-circular v-if="thirdPartyFormModal.bankDataLoading" indeterminate size="16" width="2"></v-progress-circular>
              <span v-else>{{ thirdPartyFormModal.bankOwnerName || '-' }}</span>
            </div>
            <v-btn icon x-small :disabled="!thirdPartyFormModal.bankOwnerName" @click="copyField('account_owner', thirdPartyFormModal.bankOwnerName)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'account_owner' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'account_owner' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Mandatsreferenz</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormMandateId }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormMandateId" @click="copyField('mandate_id', thirdPartyFormMandateId)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'mandate_id' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'mandate_id' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Vertragsdatum</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormContractDate }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormContractDate" @click="copyField('contract_date', thirdPartyFormContractDate)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'contract_date' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'contract_date' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <v-divider class="my-4"></v-divider>
          <div class="subtitle-2 mb-3">Poseidon</div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">IBAN (Zahlungseingänge)</div>
            <div class="form-copy-value flex-grow-1">
              <v-progress-circular v-if="thirdPartyFormModal.bankDataLoading" indeterminate size="16" width="2"></v-progress-circular>
              <span v-else>{{ thirdPartyFormModal.collectionIban || '-' }}</span>
            </div>
            <v-btn icon x-small :disabled="!thirdPartyFormModal.collectionIban" @click="copyField('collection_iban', thirdPartyFormModal.collectionIban)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'collection_iban' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'collection_iban' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">LS Text Kunde</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormLsText }}</div>
            <v-btn icon x-small @click="copyField('ls_text', thirdPartyFormLsText)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'ls_text' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'ls_text' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Creditor ID</div>
            <div class="form-copy-value flex-grow-1">{{ thirdPartyFormCreditorId || '-' }}</div>
            <v-btn icon x-small :disabled="!thirdPartyFormCreditorId" @click="copyField('creditor_id', thirdPartyFormCreditorId)">
              <v-icon small :color="thirdPartyFormModal.copiedField === 'creditor_id' ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'creditor_id' ? mdiCheck : mdiContentCopy }}</v-icon>
            </v-btn>
          </div>

          <div class="form-copy-row d-flex align-start mt-2">
            <div class="form-copy-label caption grey--text text--darken-1 mt-1">Gerät</div>
            <div class="flex-grow-1">
              <template v-if="thirdPartyFormDevices.length">
                <div
                  v-for="(device, index) in thirdPartyFormDevices"
                  :key="device.id"
                  class="d-flex align-center mb-1"
                >
                  <span class="form-copy-value flex-grow-1">{{ device.name }} {{ device.id }}</span>
                  <v-btn icon x-small @click="copyField('device_' + index, device.name + ' ' + device.id)">
                    <v-icon small :color="thirdPartyFormModal.copiedField === 'device_' + index ? 'success' : 'grey'">{{ thirdPartyFormModal.copiedField === 'device_' + index ? mdiCheck : mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </template>
              <div v-else class="d-flex align-center mb-1">
                <span class="form-copy-value flex-grow-1 grey--text font-italic">z.B. SPENDENSÄULE FS-000010</span>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>
          <div class="subtitle-2 mb-3">Terminal</div>

          <div class="form-info-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Laufzeit</div>
            <div class="form-copy-value">48 Monate</div>
          </div>
          <div class="form-info-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Hersteller</div>
            <div class="form-copy-value">digital.wolff</div>
          </div>
          <div class="form-info-row d-flex align-center mb-2">
            <div class="form-copy-label caption grey--text text--darken-1">Vertragsbeginn</div>
            <div class="form-copy-value">heute ({{ today }})</div>
          </div>

          <v-divider class="my-4"></v-divider>
          <div class="subtitle-2 mb-3">Zusatzfunktionen EMV (Händler Ebene)</div>

          <v-alert type="error" dense outlined class="mb-0">
            <strong>LÖSCHEN:</strong> „Alle Karten — Belegdruck Händler AUS"
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="personModal.show"
      max-width="650px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{ personModal.data ? `${personModal.data.first_name} ${personModal.data.last_name}` : 'Personendetails' }}</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="personModal.data && !personModal.isEditingPerson"
            icon
            small
            class="mr-1"
            @click="startEditPerson"
          >
            <v-icon small>{{ mdiPencil }}</v-icon>
          </v-btn>
          <v-btn
            icon
            @click="personModal.show = false"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="personModal.loading"
            indeterminate
            color="primary"
          ></v-progress-linear>
          <div v-else-if="personModal.error && !personModal.data">
            <v-alert type="error">
              {{ personModal.error }}
            </v-alert>
          </div>
          <div v-else-if="personModal.data">
            <!-- Person edit form: Vorname/Nachname/Adresse; other read-only values are shown inline in the ID block below -->
            <template v-if="personModal.isEditingPerson">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="personModal.personEditData.first_name"
                    label="Vorname"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="personModal.personEditData.last_name"
                    label="Nachname"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="personModal.addressEditData.address"
                    label="Straße und Hausnummer"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="personModal.addressEditData.postcode"
                    label="PLZ"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="personModal.addressEditData.locality"
                    label="Ort"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn text @click="personModal.isEditingPerson = false" class="mr-2">Abbrechen</v-btn>
                  <v-btn color="primary" :loading="personModal.savingPerson" @click="savePersonData">Speichern</v-btn>
                </v-col>
              </v-row>
            </template>

            <v-divider v-if="personModal.isEditingPerson" class="my-4"></v-divider>

            <!-- Legitimization section header -->
            <div class="d-flex align-center mb-3">
              <h3>Legitimation</h3>
              <v-spacer></v-spacer>
              <v-btn
                v-if="personModal.legitimization && personModal.legitimization.status === 'pending' && !personModal.isEditingLegitimization"
                icon
                small
                @click="startEditLegitimization"
              >
                <v-icon small>{{ mdiPencil }}</v-icon>
              </v-btn>
            </div>

            <v-alert
              v-if="personModal.legitimizationError"
              type="error"
              dense
              class="mb-3"
            >
              {{ personModal.legitimizationError }}
            </v-alert>
            <div v-else-if="personModal.legitimization">

              <!-- Legitimization read-only view: front-of-ID data in the left column, back-of-ID data in the right column -->
              <template v-if="!personModal.isEditingLegitimization">
                <v-row>
                  <v-col cols="12" sm="6">
                    <table class="caption" style="border-spacing: 4px 2px">
                      <tr>
                        <td class="grey--text pr-3">Dokumentnummer</td>
                        <td>
                          {{ personModal.legitimization.document_no || '-' }}
                          <span v-if="personModal.legitimization.document_type" class="grey--text ml-1">({{ personModal.legitimization.document_type }})</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Nachname</td>
                        <td>{{ personModal.data.last_name }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Vorname</td>
                        <td>{{ personModal.data.first_name }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Geburtsdatum</td>
                        <td>{{ personModal.legitimization.date_of_birth ? momentize(personModal.legitimization.date_of_birth) : '-' }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Geburtsort</td>
                        <td>{{ personModal.legitimization.place_of_birth || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Ablaufdatum</td>
                        <td>{{ personModal.legitimization.expiry_date ? momentize(personModal.legitimization.expiry_date) : '-' }}</td>
                      </tr>
                    </table>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <table class="caption" style="border-spacing: 4px 2px">
                      <tr>
                        <td class="grey--text pr-3">Ausstellungsdatum</td>
                        <td>{{ personModal.legitimization.issued_date ? momentize(personModal.legitimization.issued_date) : '-' }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3">Ausstellende Behörde</td>
                        <td>{{ personModal.legitimization.issuing_authority || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="grey--text pr-3" style="vertical-align: top">Adresse</td>
                        <td>
                          <template v-if="personModal.data.address">
                            {{ personModal.data.address.address }}<br>{{ personModal.data.address.postcode }} {{ personModal.data.address.locality }}
                          </template>
                          <span v-else class="grey--text">-</span>
                        </td>
                      </tr>
                    </table>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col cols="12" sm="6">
                    <strong>Status:</strong>
                    <v-icon
                      v-if="personModal.legitimization.status === 'verified'"
                      small
                      color="success"
                      class="ml-1"
                    >{{ mdiCheckCircle }}</v-icon>
                    <v-icon
                      v-else-if="personModal.legitimization.status === 'pending'"
                      small
                      color="warning"
                      class="ml-1"
                    >{{ mdiClockOutline }}</v-icon>
                    {{ personModal.legitimization.status || '-' }}
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-btn
                      v-if="personModal.legitimization.status === 'pending'"
                      color="success"
                      @click="setLegitimizationStatus('verified')"
                      :disabled="personModal.verifying"
                    >
                      {{ personModal.verifying ? 'Wird gespeichert...' : 'Als verifiziert markieren' }}
                    </v-btn>
                    <v-btn
                      v-else-if="personModal.legitimization.status === 'verified'"
                      color="warning"
                      @click="setLegitimizationStatus('pending')"
                      :disabled="personModal.verifying"
                    >
                      {{ personModal.verifying ? 'Wird gespeichert...' : 'Auf ausstehend zurücksetzen' }}
                    </v-btn>
                    <div v-if="personModal.legitimization.status === 'pending'" class="mt-2 text-caption">
                      Ich habe die Dokumente zur Identifikation überprüft.
                    </div>
                  </v-col>
                </v-row>
              </template>

              <!-- Legitimization edit form (only for pending) -->
              <template v-else>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="personModal.legitEditData.document_type"
                      label="Dokumenttyp"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="personModal.legitEditData.document_no"
                      label="Dokumentnummer"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="personModal.legitEditData.issuing_authority"
                      label="Ausstellende Behörde"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="personModal.legitEditData.place_of_birth"
                      label="Geburtsort"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <text-field-date-picker
                      v-model="personModal.legitEditData.date_of_birth"
                      label="Geburtsdatum"
                    ></text-field-date-picker>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <text-field-date-picker
                      v-model="personModal.legitEditData.issued_date"
                      label="Ausstellungsdatum"
                    ></text-field-date-picker>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <text-field-date-picker
                      v-model="personModal.legitEditData.expiry_date"
                      label="Ablaufdatum"
                    ></text-field-date-picker>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn text @click="personModal.isEditingLegitimization = false" class="mr-2">Abbrechen</v-btn>
                    <v-btn color="primary" :loading="personModal.savingLegitimization" @click="saveLegitimizationData">Speichern</v-btn>
                  </v-col>
                </v-row>
              </template>

            </div>
            <div v-else>
              <span class="grey--text">Keine Legitimationsdaten vorhanden.</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  FETCH_CONTACTS,
  FETCH_CONTRACT_CUSTOMER_DATA,
  FETCH_CONTRACT_PERSON_DATA,
  FETCH_CONTRACT_RELATED_FILES,
  FETCH_CONTRACTS,
  FETCH_GENERATED_CONTRACT_BUNDLES,
  FETCH_GLOBAL_DEVICES,
  FETCH_ORGANIZATIONS, GENERATE_CONTRACT,
  REGENERATE_CONTRACT, UPLOAD_CONTRACT_RELATED_FILE,
} from "@/store/action-types"
import { mapGetters, mapState } from "vuex"
import moment from "moment"
import {
  mdiReload,
  mdiDownloadOutline,
  mdiDownload,
  mdiFileDocumentPlusOutline,
  mdiUpload,
  mdiClose,
  mdiCheckCircle,
  mdiClockOutline,
  mdiPencil,
  mdiClipboardOutline,
  mdiContentCopy,
  mdiCheck,
  mdiOpenInNew,
} from "@mdi/js"
import DeviceIcon from "@/components/icons/DeviceIcon.vue"
import TextFieldDatePicker from "@/components/UI/TextFieldDatePicker.vue"
import { downloadBlob } from "@/lib/file-tools"
import {
  RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES,
  RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM
} from "@/store/contracts"
import { CONTRACT_PRODUCT_CLASSES, ECOM_DEVICE_LIST } from "@/lib/device-db"
import { INVALIDATE_CACHE } from "@/modules/common/store/action-types"
import { SET_ERROR_MESSAGE } from "@/modules/common/store/mutation-types"
import {
  CACHE_CONTRACTS,
  CACHE_CONTRACT_CUSTOMER_DATA,
  CACHE_CONTRACT_PERSON_DATA,
  CACHE_GENERATED_CONTRACT_BUNDLES,
} from "@/store/cache-types"

export default {
  name: 'AdminContracts',
  components: {
    DeviceIcon,
    TextFieldDatePicker,
  },
  data () {
    return {
      RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES,
      RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM,
      CONTRACT_PRODUCT_CLASSES,
      ECOM_DEVICE_LIST,
      mdiReload,
      mdiDownloadOutline,
      mdiFileDocumentPlusOutline,
      mdiUpload,
      mdiClose,
      mdiCheckCircle,
      mdiClockOutline,
      mdiPencil,
      mdiClipboardOutline,
      mdiContentCopy,
      mdiCheck,
      mdiOpenInNew,
      activeUploadItem: null,
      activeUploadType: null,
      pollingIntervalIds: [],
      uploadFile: null,
      csvImportFile: null,
      openPendingPanels: [],
      pendingBankAccounts: {},
      loadingBankAccounts: {},
      pendingCreditorIds: {},
      pendingShortOrganizationNames: {},
      acceptingCustomerId: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      deviceEditDialog: {
        show: false,
        customer: null,
        editData: {},
        saving: false,
      },
      personModal: {
        show: false,
        loading: false,
        data: null,
        isEditingPerson: false,
        personEditData: null,
        addressEditData: null,
        savingPerson: false,
        legitimization: null,
        legitimizationUrl: null,
        legitimizationError: null,
        isEditingLegitimization: false,
        legitEditData: null,
        savingLegitimization: false,
        verifying: false,
        error: null
      },
      thirdPartyFormModal: {
        show: false,
        item: null,
        copiedField: null,
        bankDataLoading: false,
        bankIban: null,
        bankOwnerName: null,
        collectionIban: null,
        contractCreditorId: null,
        contractPurpose: null,
        contractNumber: null,
        contractAddress: null,
      }
    }
  },
  computed: {
    ...mapState('staticConfiguration', ['enumsByName']),
    ...mapGetters('organization', ['allOrganizations']),
    ...mapGetters('contracts', ['contractCustomerData', 'contractPersonDataByUrl', 'contractsByOrganization']),
    pendingContractRequests () {
      return this.contractCustomerData.filter(item => !item.organizationId)
    },
    activeContracts () {
      return this.contractCustomerData.filter(item => item.organizationId)
    },
    contractRequestFormUrl () {
      return this.$router.resolve({ name: 'contract.request' }).href
    },
    headers () {
      return [
        {
          text: 'Organisation',
          value: 'organization_name',
        },
        {
          text: 'Erstellt am',
          value: 'created_at'
        },
        {
          text: 'Hardware',
          value: '_offer_devices'
        },
        {
          text: 'eCom',
          value: 'relatedFileEcom'
        },
        {
          text: 'Person 1',
          value: '_person1'
        },
        {
          text: 'Person 2',
          value: '_person2'
        },
        {
          text: 'Person 3',
          value: '_person3'
        },
        {
          text: '',
          value: '_actions'
        }
      ]
    },
    thirdPartyFormAddress () {
      if (!this.thirdPartyFormModal.contractAddress) return null
      const a = this.thirdPartyFormModal.contractAddress
      return {
        address: a.address || null,
        street: a.street || null,
        house_number: a.house_number || null,
        postcode: a.postcode || null,
        locality: a.locality || null,
      }
    },
    thirdPartyFormAddressSplit () {
      const a = this.thirdPartyFormAddress
      if (!a) return { street: '', nr: '' }
      if (a.street !== undefined || a.house_number !== undefined) {
        return { street: a.street || '', nr: a.house_number || '' }
      }
      const addr = a.address || ''
      if (!addr) return { street: '', nr: '' }
      const match = addr.match(/^(.+)\s+(\d+\S*)$/)
      if (match) return { street: match[1], nr: match[2] }
      return { street: addr, nr: '' }
    },
    thirdPartyFormMandateId () {
      return 'KNB2026'
    },
    thirdPartyFormContractDate () {
      const item = this.thirdPartyFormModal.item
      if (!item || !item.created_at) return ''
      return this.momentize(item.created_at)
    },
    today () {
      return moment().format('DD.MM.YYYY')
    },
    thirdPartyFormCreditorId () {
      return this.thirdPartyFormModal.contractCreditorId || null
    },
    thirdPartyFormLsText () {
      return this.thirdPartyFormModal.contractPurpose || ''
    },
    thirdPartyFormDevices () {
      const item = this.thirdPartyFormModal.item
      if (!item) return []
      const allDevices = (this.$store.state.device && this.$store.state.device.devices) || []
      return allDevices.filter(device =>
        device.contracts && device.contracts.some(dc => dc.contract && dc.contract.organization === item.organization)
      )
    },
  },
  methods: {
    mdiDownload () {
      return mdiDownload
    },
    apiErrorMessage (error) {
      const data = error?.response?.data
      if (data) {
        return typeof data === 'string' ? data : JSON.stringify(data)
      }
      return error?.message ?? String(error)
    },
    openThirdPartyFormModal (item) {
      this.thirdPartyFormModal = {
        show: true,
        item,
        copiedField: null,
        bankDataLoading: false,
        bankIban: null,
        bankOwnerName: null,
        collectionIban: null,
        contractCreditorId: null,
        contractPurpose: null,
        contractNumber: null,
        contractAddress: null,
      }
      this.fetchThirdPartyBankData(item)
    },
    async fetchThirdPartyBankData (item) {
      this.thirdPartyFormModal.bankDataLoading = true
      try {
        // Customer detail: creditor ID, purpose, and possibly contract reference for slug
        try {
          const customerResponse = await this.$store.getters.restApi.get(item.url)
          const cd = customerResponse.data
          this.thirdPartyFormModal.contractCreditorId = cd.creditor_id || null
          this.thirdPartyFormModal.contractPurpose = cd.purpose_on_account_statement || null
          if (cd.contract && !this.thirdPartyFormModal.contractNumber) {
            this.thirdPartyFormModal.contractNumber = String(cd.contract).split('/').filter(Boolean).pop() || null
          }
        } catch (_) {}

        // Address from kbcontract
        try {
          const addrResponse = await this.$store.getters.restApi.get('/contract/application/address/', { params: { customer: item.id } })
          const addrList = Array.isArray(addrResponse.data) ? addrResponse.data : (addrResponse.data?.results || [])
          this.thirdPartyFormModal.contractAddress = addrList[0] || null
        } catch (_) {}

        // Customer bank accounts — type 'sepa' for mandate, type 'payment' for collection
        try {
          const cbaResponse = await this.$store.getters.restApi.get('/contract/application/customer_bank_account/', { params: { customer: item.id } })
          const cbaList = Array.isArray(cbaResponse.data) ? cbaResponse.data : (cbaResponse.data?.results || [])
          const sepaAccount = cbaList.find(a => a.types && a.types.includes('sepa')) || null
          const paymentAccount = cbaList.find(a => a.types && a.types.includes('payment')) || null
          if (sepaAccount) {
            this.thirdPartyFormModal.bankIban = sepaAccount.iban || null
            this.thirdPartyFormModal.bankOwnerName = sepaAccount.owner || null
          }
          if (paymentAccount) {
            this.thirdPartyFormModal.collectionIban = paymentAccount.iban || null
          }
        } catch (_) {}
      } finally {
        this.thirdPartyFormModal.bankDataLoading = false
      }
    },
    copyField (key, value) {
      if (!value) return
      navigator.clipboard.writeText(value)
      this.thirdPartyFormModal.copiedField = key
      setTimeout(() => {
        if (this.thirdPartyFormModal.copiedField === key) {
          this.thirdPartyFormModal.copiedField = null
        }
      }, 1500)
    },
    ...mapGetters('contracts', ['getContractPersonByUrl']),
    momentize (dateTime) {
      return moment(dateTime).format('DD.MM.YYYY')
    },
    async generateContract (customer) {
      const previousBundleUrl = customer.generatedContractBundle?.url ?? null
      await this.$store.dispatch('contracts/' + GENERATE_CONTRACT, { customerUrl: customer.url })
      customer.isContractRegenerating = true
      this.pollForNewBundle(customer, previousBundleUrl)
    },
    async regenerateContract (customer) {
      const previousBundleUrl = customer.generatedContractBundle?.url ?? null
      await this.$store.dispatch('contracts/' + REGENERATE_CONTRACT, { customerUrl: customer.url })
      customer.isContractRegenerating = true
      this.pollForNewBundle(customer, previousBundleUrl)
    },
    pollForNewBundle (customer, previousBundleUrl) {
      const MAX_ATTEMPTS = 60 // 5 minutes at 5s intervals
      let attempts = 0

      const intervalId = setInterval(async () => {
        attempts++
        if (attempts > MAX_ATTEMPTS) {
          this.clearPollingInterval(intervalId)
          customer.isContractRegenerating = false
          this.snackbar = {
            show: true,
            message: 'Die Vertragserstellung dauert länger als erwartet. Bitte prüfe den Status später erneut.',
            color: 'error'
          }
          return
        }

        try {
          await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_GENERATED_CONTRACT_BUNDLES })
          await this.$store.dispatch('contracts/' + FETCH_GENERATED_CONTRACT_BUNDLES)
          await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_CUSTOMER_DATA })
          await this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA)

          const updatedCustomer = this.contractCustomerData.find(entry => entry.url === customer.url)

          if (updatedCustomer?.contract_generation_status === 'failed') {
            this.clearPollingInterval(intervalId)
            customer.isContractRegenerating = false
            this.snackbar = {
              show: true,
              message: `Vertragserstellung fehlgeschlagen: ${updatedCustomer.contract_generation_error || 'Unbekannter Fehler'}`,
              color: 'error'
            }
            return
          }

          const bundles = this.$store.getters['contracts/generatedContractBundlesByCustomer'][customer.url] ?? []
          const latest = bundles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

          if (latest && latest.url !== previousBundleUrl) {
            this.clearPollingInterval(intervalId)
            customer.isContractRegenerating = false
            this.snackbar = {
              show: true,
              message: 'Vertrag wurde erfolgreich erstellt und kann heruntergeladen werden.',
              color: 'success'
            }
          }
        } catch (_) {
          // ignore transient polling errors
        }
      }, 5000)

      this.pollingIntervalIds.push(intervalId)
    },
    clearPollingInterval (intervalId) {
      clearInterval(intervalId)
      this.pollingIntervalIds = this.pollingIntervalIds.filter(id => id !== intervalId)
    },
    async downloadContract (item) {
      if (!item.generatedContractBundle) {
        return
      }

      try {
        const bundle = item.generatedContractBundle
        const response = await this.$store.getters.restApi.get(`${bundle.url}download/`, { responseType: 'blob' })
        downloadBlob(response.data, bundle.name, response.headers['content-type'])
      } catch (error) {
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      }
    },
    initializeAttachmentUpload (item, type) {
      this.activeUploadItem = item
      this.activeUploadType = type
      this.$refs.relatedFileChooser.$refs.input.click()
    },
    async performFileUpload () {
      const name = `Anlage ${this.activeUploadType} - ${this.uploadFile.name}`
      await this.$store.dispatch('contracts/' + UPLOAD_CONTRACT_RELATED_FILE, {
        file: this.uploadFile,
        name,
        attachmentNumber: this.activeUploadType,
        customerUrl: this.activeUploadItem.url
      })

      this.resetUploadData()
    },
    resetUploadData () {
      this.activeUploadItem = null
      this.activeUploadType = null
      this.uploadFile = null
    },
    openCsvImport () {
      this.$refs.csvImportChooser.$refs.input.click()
    },
    pendingPersonTypeLabel (type) {
      return { contact: 'Kontakt', administrator: 'Administrator', invoice: 'Rechnung', authorized_signer: 'Zeichnungsber.' }[type] || type
    },
    pendingBankTypeLabel (type) {
      return { payment: 'Zahlungseingang', sepa: 'SEPA-Lastschrift' }[type] || type
    },
    async fetchBankAccountsForCustomer (item) {
      if (this.pendingBankAccounts[item.url] || this.loadingBankAccounts[item.url]) return
      this.$set(this.loadingBankAccounts, item.url, true)
      try {
        const response = await this.$store.getters.restApi.get(
          '/contract/application/customer_bank_account/',
          { params: { customer: item.id } }
        )
        const all = response.data.results ?? response.data
        const typeOrder = ['payment', 'sepa']
        const filtered = all
          .filter(ba => ba.types && ba.types.some(t => typeOrder.includes(t)))
          .sort((a, b) => {
            const ai = typeOrder.findIndex(t => a.types.includes(t))
            const bi = typeOrder.findIndex(t => b.types.includes(t))
            return ai - bi
          })
        this.$set(this.pendingBankAccounts, item.url, filtered)
      } catch (error) {
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.$set(this.loadingBankAccounts, item.url, false)
      }
    },
    effectiveCreditorId (item) {
      const edited = this.pendingCreditorIds[item.id]
      return edited !== undefined ? edited : (item.creditor_id || '')
    },
    async acceptContractRequest (item) {
      this.acceptingCustomerId = item.id
      try {
        const editedCreditorId = this.pendingCreditorIds[item.id]
        const editedShortOrganizationName = this.pendingShortOrganizationNames[item.id]
        const patch = {}
        if (editedCreditorId !== undefined && editedCreditorId !== item.creditor_id) {
          patch.creditor_id = editedCreditorId
        }
        if (editedShortOrganizationName !== undefined && editedShortOrganizationName !== item.short_organization_name) {
          patch.short_organization_name = editedShortOrganizationName
        }
        if (Object.keys(patch).length) {
          await this.$store.getters.restApi.patch(item.url, patch)
        }
        await this.$store.getters.restApi.post(
          `/contract/application/customer/${item.id}/accept_contract_request/`
        )
        await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_CUSTOMER_DATA })
        await this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA)
        this.openPendingPanels = []
        this.pendingBankAccounts = {}
        this.loadingBankAccounts = {}
        this.pendingCreditorIds = {}
        this.pendingShortOrganizationNames = {}
        this.snackbar = { show: true, message: 'Vertragsanfrage erfolgreich angenommen', color: 'success' }
      } catch (error) {
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
        this.snackbar = { show: true, message: 'Fehler beim Annehmen der Vertragsanfrage', color: 'error' }
      } finally {
        this.acceptingCustomerId = null
      }
    },
    async importCsv () {
      if (!this.csvImportFile) {
        return
      }

      try {
        const formData = new FormData()
        formData.append('file', this.csvImportFile)

        await this.$store.getters.restApi.post(
          '/contract/application/customer/import_csv/',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        this.snackbar = {
          show: true,
          message: 'CSV-Datei erfolgreich importiert',
          color: 'success'
        }

        // Invalidate caches so fetches return fresh data
        await Promise.allSettled([
          this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACTS }),
          this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_CUSTOMER_DATA }),
          this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_PERSON_DATA }),
        ])

        // Reload contract data
        await Promise.allSettled([
          this.$store.dispatch('contracts/' + FETCH_CONTRACTS),
          this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA),
          this.$store.dispatch('contracts/' + FETCH_CONTRACT_PERSON_DATA),
        ])
      } catch (error) {
        const responseData = error.response?.data ? JSON.stringify(error.response.data) : 'No data'
        const responseStatus = error.response?.status || 'No status'
        const errorMessage = `Import fehlgeschlagen. Response Status: ${responseStatus}. Data: ${responseData}`

        this.snackbar = {
          show: true,
          message: errorMessage.substring(0, 200),
          color: 'error'
        }
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.csvImportFile = null
      }
    },
    async openPersonModal (personUrl) {
      if (!personUrl) {
        return
      }

      this.personModal = {
        show: true,
        loading: true,
        data: null,
        isEditingPerson: false,
        personEditData: null,
        addressEditData: null,
        savingPerson: false,
        legitimization: null,
        legitimizationUrl: null,
        legitimizationError: null,
        isEditingLegitimization: false,
        legitEditData: null,
        savingLegitimization: false,
        verifying: false,
        error: null
      }

      try {
        const response = await this.$store.getters.restApi.get(personUrl)
        this.personModal.data = response.data
        this.personModal.loading = false
      } catch (error) {
        this.personModal.error = 'Fehler beim Laden der Personendetails'
        this.personModal.loading = false
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
        return
      }

      // Attach legitimization data from person response
      if (this.personModal.data.legitimization) {
        const legit = this.personModal.data.legitimization
        if (typeof legit === 'object') {
          // Already embedded in the person response — use directly
          this.personModal.legitimization = legit
          this.personModal.legitimizationUrl = legit.url ?? null
        } else {
          // URL string or integer ID — fetch separately
          try {
            const raw = legit.toString()
            const legitimizationId = raw.startsWith('http') ? raw.split('/').filter(Boolean).pop() : raw
            const legitimizationUrl = `contract/application/legitimization/${legitimizationId}/`
            this.personModal.legitimizationUrl = legitimizationUrl
            const legitResponse = await this.$store.getters.restApi.get(legitimizationUrl)
            this.personModal.legitimization = legitResponse.data
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Legitimization fetch failed:', error?.response?.status, error?.response?.data ?? error?.message)
            this.personModal.legitimizationError = `Fehler beim Laden der Legitimationsdaten (${error?.response?.status ?? error?.message ?? 'unbekannt'})`
          }
        }
      }
    },
    async setLegitimizationStatus (status) {
      if (!this.personModal.legitimizationUrl) {
        return
      }

      this.personModal.verifying = true

      try {
        await this.$store.getters.restApi.patch(
          this.personModal.legitimizationUrl,
          { status }
        )

        this.personModal.legitimization.status = status

        this.snackbar = {
          show: true,
          message: status === 'verified' ? 'Legitimation erfolgreich bestätigt' : 'Legitimation zurückgesetzt',
          color: 'success'
        }
      } catch (error) {
        this.snackbar = {
          show: true,
          message: 'Fehler beim Ändern des Legitimationsstatus',
          color: 'error'
        }
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.personModal.verifying = false
      }
    },
    openDeviceEdit (customer) {
      const editData = {}
      Object.keys(this.CONTRACT_PRODUCT_CLASSES).forEach(key => {
        editData[key.toLowerCase()] = customer.products_requested[key.toLowerCase()] ?? 0
      })
      this.deviceEditDialog = {
        show: true,
        customer,
        editData,
        saving: false,
      }
    },
    async saveDeviceData () {
      this.deviceEditDialog.saving = true
      try {
        const cleaned = Object.fromEntries(
          Object.entries(this.deviceEditDialog.editData).filter(([_, v]) => Number(v) > 0)
        )
        await this.$store.getters.restApi.patch(this.deviceEditDialog.customer.url, { products_requested: cleaned })
        this.deviceEditDialog.show = false
        await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_CUSTOMER_DATA })
        await this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA)
        this.snackbar = { show: true, message: 'Geräteangebot gespeichert', color: 'success' }
      } catch (error) {
        this.snackbar = { show: true, message: 'Fehler beim Speichern des Geräteangebots', color: 'error' }
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.deviceEditDialog.saving = false
      }
    },
    startEditPerson () {
      const d = this.personModal.data
      this.personModal.personEditData = {
        first_name: d.first_name,
        last_name: d.last_name,
      }
      this.personModal.addressEditData = {
        address: d.address?.address ?? '',
        postcode: d.address?.postcode ?? '',
        locality: d.address?.locality ?? '',
      }
      this.personModal.isEditingPerson = true
    },
    async savePersonData () {
      this.personModal.savingPerson = true
      try {
        await this.$store.getters.restApi.patch(this.personModal.data.url, this.personModal.personEditData)

        const existingAddress = this.personModal.data.address
        let savedAddress
        if (existingAddress) {
          const response = await this.$store.getters.restApi.patch(existingAddress.url, this.personModal.addressEditData)
          savedAddress = response.data
        } else {
          const response = await this.$store.getters.restApi.post('/contract/application/address/', {
            person: this.personModal.data.url,
            type: 'authorizer',
            ...this.personModal.addressEditData,
          })
          savedAddress = response.data
        }

        this.personModal.data = { ...this.personModal.data, ...this.personModal.personEditData, address: savedAddress }
        this.personModal.isEditingPerson = false
        await this.$store.dispatch(INVALIDATE_CACHE, { key: CACHE_CONTRACT_PERSON_DATA })
        await this.$store.dispatch('contracts/' + FETCH_CONTRACT_PERSON_DATA)
        this.snackbar = { show: true, message: 'Personendaten gespeichert', color: 'success' }
      } catch (error) {
        this.snackbar = { show: true, message: 'Fehler beim Speichern der Personendaten', color: 'error' }
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.personModal.savingPerson = false
      }
    },
    startEditLegitimization () {
      const l = this.personModal.legitimization
      this.personModal.legitEditData = {
        document_type: l.document_type ?? '',
        document_no: l.document_no ?? '',
        issuing_authority: l.issuing_authority ?? '',
        place_of_birth: l.place_of_birth ?? '',
        date_of_birth: l.date_of_birth ?? null,
        issued_date: l.issued_date ?? null,
        expiry_date: l.expiry_date ?? null,
      }
      this.personModal.isEditingLegitimization = true
    },
    async saveLegitimizationData () {
      this.personModal.savingLegitimization = true
      try {
        await this.$store.getters.restApi.patch(this.personModal.legitimizationUrl, this.personModal.legitEditData)
        this.personModal.legitimization = { ...this.personModal.legitimization, ...this.personModal.legitEditData }
        this.personModal.isEditingLegitimization = false
        this.snackbar = { show: true, message: 'Legitimationsdaten gespeichert', color: 'success' }
      } catch (error) {
        this.snackbar = { show: true, message: 'Fehler beim Speichern der Legitimationsdaten', color: 'error' }
        this.$store.commit(SET_ERROR_MESSAGE, { message: this.apiErrorMessage(error) })
      } finally {
        this.personModal.savingLegitimization = false
      }
    }
  },
  watch: {
    openPendingPanels (newVal, oldVal) {
      const newlyOpened = newVal.filter(i => !(oldVal || []).includes(i))
      newlyOpened.forEach(i => {
        if (this.pendingContractRequests[i]) {
          this.fetchBankAccountsForCustomer(this.pendingContractRequests[i])
        }
      })
    },
    uploadFile (newValue, oldValue) {
      if (!newValue || newValue === oldValue) {
        return
      }

      this.performFileUpload()
    },
    csvImportFile (newValue, oldValue) {
      if (!newValue || newValue === oldValue) {
        return
      }

      this.importCsv()
    }
  },
  async mounted () {
    await Promise.allSettled([
      this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
      this.$store.dispatch('admin/' + FETCH_CONTACTS),
      this.$store.dispatch('device/' + FETCH_GLOBAL_DEVICES),
      this.$store.dispatch('contracts/' + FETCH_CONTRACTS),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_PERSON_DATA),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_RELATED_FILES),
      this.$store.dispatch('contracts/' + FETCH_GENERATED_CONTRACT_BUNDLES),
    ])
  },
  beforeDestroy () {
    this.pollingIntervalIds.forEach(intervalId => clearInterval(intervalId))
    this.pollingIntervalIds = []
  },
}
</script>

<style lang="scss" scoped>
.grid-gap {
  grid-gap: 8px;
}

.related-devices {
  width: 40px;
}

.related-file-name {
  max-width: 200px;
}

.person-link {
  color: var(--v-primary-base);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--v-primary-base);
  }
}

.form-copy-label {
  min-width: 120px;
  flex-shrink: 0;
}

.form-copy-value {
  word-break: break-word;
}
</style>
